/* Review-side Motion integration. Loading this file does not activate offers. */
(() => {
  async function refreshCart() {
    const updates = [];
    document.dispatchEvent(new CustomEvent('suggestive-sales:refresh-cart', {
      detail: {register(promise) { updates.push(Promise.resolve(promise)); }}
    }));
    if (!updates.length) throw new Error('No connected basket display is available');
    let timer;
    try {
      await Promise.race([
        Promise.all(updates),
        new Promise((_, reject) => { timer = setTimeout(() => reject(new Error('Basket display refresh timed out')), 15000); })
      ]);
    } finally { clearTimeout(timer); }
  }
  let active = null, starting = null;
  function start() {
    if (active) return Promise.resolve(active);
    if (starting) return starting;
    starting = initialize().finally(() => { starting = null; });
    return starting;
  }
  async function initialize() {
    const node = document.querySelector('[data-suggestive-sales="published-configuration"]');
    if (!node || !window.SuggestiveSales?.bootstrapThemeConnector) return {enabled:false};
    const containers = (exclude) => [...document.querySelectorAll('[data-suggestive-sales-placement]')].filter(host => !exclude?.contains(host)).map(host => ({
      host, placement:host.dataset.suggestiveSalesPlacement,
      ...(host.dataset.productId ? {productId:host.dataset.productId} : {})
    }));
    const productHandle = document.querySelector('[data-suggestive-sales-placement="product"]')?.dataset.productHandle;
    let abandoned = false;
    const abandon = () => { abandoned = true; };
    window.addEventListener('pagehide', abandon, {once:true});
    let connector;
    try {
      connector = await window.SuggestiveSales.bootstrapThemeConnector({
      enabled:true, presentationVersion:2, closeOfferAfterAcceptance:false, retainOfferAfterAcceptance:true, basketSuggestionLimit:5,
      onOfferClosed:() => refreshCart().catch(() => {}).then(() => document.dispatchEvent(new CustomEvent('cart:open'))),
      node, root:window.Shopify.routes.root, containers:containers(), refreshCart,
      membership:{sectionId:'suggestive-sales-membership', ...(productHandle ? {productPath:'products/' + productHandle} : {})},
      formatMoney:(amount,currency)=>new Intl.NumberFormat(document.documentElement.lang || 'en-US',{style:'currency',currency}).format(amount/100)
      });
    } finally { window.removeEventListener('pagehide', abandon); }
    if (abandoned) { connector.destroy?.(); return {enabled:false}; }
    if (!connector.enabled) return connector;
    const seen = new WeakSet();
    let serial = 0;
    const report = () => document.dispatchEvent(new CustomEvent('suggestive-sales:integration-error'));
    const added = bridgeEvent => {
      const event = bridgeEvent.detail?.originalEvent;
      if (!event || typeof bridgeEvent.detail.register !== 'function') return;
      const form = event.target, product = event.detail?.product, button = event.detail?.addToCartBtn;
      if (!(form instanceof HTMLFormElement) || !form.isConnected || !button || !form.contains(button) ||
          !product || typeof product !== 'object' || product.status || seen.has(product) ||
          !/^[1-9]\d*$/.test(String(product.product_id)) || !/^[1-9]\d*$/.test(String(product.variant_id || product.id))) return;
      seen.add(product);
      const offerTask = connector.confirmOrdinaryPurchase({kind:'ordinary',status:'confirmed',accelerated:false,
        eventId:'suggestive-sales-motion-' + (++serial), productId:String(product.product_id),focusTarget:button}).catch(report);
      bridgeEvent.detail.register(offerTask);
      return offerTask;
    };
    const refresh = () => connector.refresh().catch(report);
    const remount = () => connector.replaceContainers(containers()).catch(report);
    // Unload fires while old markup can still be connected; explicitly exclude it.
    const unload = event => connector.replaceContainers(containers(event.target)).catch(report);

    document.addEventListener('cart:updated', refresh);
    document.addEventListener('shopify:section:load', remount);
    document.addEventListener('shopify:section:unload', unload);
    const destroy = () => {
      document.removeEventListener('suggestive-sales:ordinary-add', earlyAdded);
      document.removeEventListener('cart:updated', refresh);
      document.removeEventListener('shopify:section:load', remount);
      document.removeEventListener('shopify:section:unload', unload);
      window.removeEventListener('pagehide', destroy);
      connector.destroy(); active = null;
    };
    window.addEventListener('pagehide', destroy, {once:true});
    active = {enabled:true,destroy,handleAdded:added};
    return active;
  }
  // Register before async catalog/bootstrap work: retain the first customer add.
  const earlyAdded = event => {
    if (typeof event.detail?.register !== 'function') return;
    if (active) return active.handleAdded(event);
    const pending = start().then(() => active?.handleAdded(event));
    event.detail.register(pending);
  };
  document.addEventListener('suggestive-sales:ordinary-add', earlyAdded);
  window.addEventListener('pagehide', () => document.removeEventListener('suggestive-sales:ordinary-add', earlyAdded));
  // A restored history page does not run DOMContentLoaded or deferred scripts again.
  window.addEventListener('pageshow', event => {
    if (!event.persisted) return;
    document.addEventListener('suggestive-sales:ordinary-add', earlyAdded);
    Promise.resolve(starting).then(() => start()).catch(() =>
      document.dispatchEvent(new CustomEvent('suggestive-sales:integration-error')));
  });
  window.SuggestiveSalesThemeAdapter = Object.freeze({refreshCart,start});
})();
